import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import {
  CampaignTemplate,
  SimulationPlanData,
  SimulationSchedulerItem,
} from '@/client/campaigns';
import { Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TargetFormValue } from '@/common/types/targets.type';
import { CatalogueSelectorModal } from '@/components/campaigns';
import { AddButton } from '@/components/campaigns/simulations/AddButton';
import { DpaAcceptanceModal } from '@/components/campaigns/simulations/modals/DpaAcceptanceModal';
import { PlanWithAssistanceModal } from '@/components/campaigns/simulations/modals/PlanWithAssistanceModal';
import { SendTestEmailForm } from '@/components/campaigns/simulations/SendTestEmailForm';
import { SimulationTemplateCard } from '@/components/campaigns/simulations/SimulationTemplateCard';
import { useAccount } from '@/hooks/query';
import { useAcceptDpaAmendment } from '@/hooks/query/osint.hooks';
import { useAppSelector } from '@/hooks/store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { FlexContainer } from '@/ui';
import { AppButton } from '@/ui/buttons';
import { AppCalendarWithCustomTimepicker } from '@/ui/calendar/AppCalendarWithCustomTimepicker';
import { TargetEntitiesSelector } from '@/ui/target-selector';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { EditIcon, WarningTriangleIcon } from '@/utils/helpers/icons.helper';
import {
  getDefaultTargetGroup,
  getTargetsAndExcludes,
  processIncludeExcludeTargetEntities,
} from '@/utils/targets';
import { getAppAbsoluteUrl } from '@/utils/url';
import { AxiosError } from 'axios';
import moment from 'moment';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  width: 649px;

  label {
    font-weight: 400;
  }
`;

const SimulationSelectorContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EditIconButton = styled.div`
  svg path {
    fill: var(--green-color);
  }

  cursor: pointer;
  padding: var(--xxsmall-padding);
  margin-top: var(--xxsmall-padding);
`;

const SimulationTemplateDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  border: 1px solid var(--gray-light);
  border-radius: var(--box-border-radius);
  background: white;
  min-height: 80px;
  padding-right: var(--medium-padding);
`;

const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--medium-padding);
`;

const StyledAccordion = styled(Accordion)`
  .p-accordion-tab {
    border: 1px solid var(--gray-moderate);
    border-radius: var(--xsmall-border-radius);
    overflow: hidden;
  }

  .p-accordion-header-link {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: var(--medium-padding) var(--default-padding-small);
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    font-size: var(--small-font-size);
    background: transparent;
    border: none;
  }

  .p-accordion-toggle-icon {
    margin-right: 0;
  }

  .p-accordion-content {
    padding: 0 var(--default-padding-small) var(--default-padding-small);
    border: none;
  }
`;

const StyledUnverifiedDomainWarning = styled.div`
  width: 100%;
  background-color: var(--yellow-pale);
  border-radius: var(--small-border-radius);
  padding: var(--default-padding-small) var(--default-padding);
  margin-top: var(--medium-padding);
  padding-left: var(--default-padding-small);
  display: flex;
  gap: var(--xsmall-padding);
  align-items: flex-start;

  svg {
    flex-shrink: 0;
  }

  .warning-text {
    display: flex;
    flex-direction: column;
    gap: var(--small-padding);

    p {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      margin: 0;
    }

    .warning-link {
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      font-weight: 400;
      color: var(--black-main);
      cursor: pointer;
      text-decoration: none;
      border-bottom: 2px solid var(--gray-buttom-border);
      background: none;
      padding-bottom: 6px;
    }
  }
`;

type PlanSimulationModalProps = {
  selectedTask: SimulationSchedulerItem | null;
  isOpen: boolean;
  onClose: () => void;
  onPlanSimulation: (data: SimulationPlanData) => void;
  selectedCampaignTemplate?: CampaignTemplate;
  isLoadingCampaignTemplate?: boolean;
  isRecommendedSimulation?: boolean;
};

export const PlanSimulationModal: React.FC<PlanSimulationModalProps> = ({
  isOpen,
  selectedTask,
  onClose,
  onPlanSimulation,
  selectedCampaignTemplate,
  isRecommendedSimulation = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { canUseFeature } = useFeatureFlag();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(currentAccount, currentUser);
  const [shouldPoll, setShouldPoll] = useState(false);
  const { account: freshAccount, refetch: refetchAccount } = useAccount({
    accountId: currentAccount?.id,
    refetchInterval: shouldPoll ? 10_000 : false,
  });
  const { acceptDpa } = useAcceptDpaAmendment(currentAccount?.id ?? '');
  const [showCampaignTemplatesModal, setShowCampaignTemplatesModal] =
    useState<boolean>(false);
  const [showAISimulationModal, setShowAISimulationModal] =
    useState<boolean>(false);
  const [showDpaAcceptanceModal, setShowDpaAcceptanceModal] =
    useState<boolean>(false);

  // UX: keep the "Data is being prepared" card visible for at least 5 seconds
  // so fast transitions (preparing → complete/failed) don't flash too quickly
  const MIN_PREPARING_DISPLAY_MS = 5_000;
  const preparingVisibleSince = useRef<number | null>(null);
  const [effectiveStatus, setEffectiveStatus] = useState<string | undefined>(
    freshAccount?.osintScrapingStatus,
  );

  useEffect(() => {
    const rawStatus = freshAccount?.osintScrapingStatus;

    if (rawStatus === 'in-progress') {
      if (!preparingVisibleSince.current) {
        preparingVisibleSince.current = Date.now();
      }
      setEffectiveStatus('in-progress');
      return;
    }

    if (preparingVisibleSince.current && rawStatus) {
      const elapsed = Date.now() - preparingVisibleSince.current;
      const remaining = MIN_PREPARING_DISPLAY_MS - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => {
          preparingVisibleSince.current = null;
          setEffectiveStatus(rawStatus);
        }, remaining);
        return () => clearTimeout(timer);
      }
    }

    preparingVisibleSince.current = null;
    setEffectiveStatus(rawStatus);
  }, [freshAccount?.osintScrapingStatus]);
  const [excludedDomainUnverifiedCount, setExcludedDomainUnverifiedCount] =
    useState<number>(0);
  const [formData, setFormData] = useState<SimulationPlanData>({
    id: selectedTask?.id,
    campaignTemplateId: undefined,
    difficulty: undefined,
    phishingSign: undefined,
    name: undefined,
    startDate: undefined,
    endDate: undefined,
    targetGroup: getDefaultTargetGroup(currentAccount as Account),
    isGeneratedWithAI: selectedTask?.isGeneratedWithAI,
  });

  useEffect(() => {
    if (isOpen) {
      refetchAccount();
    } else {
      setShouldPoll(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const inProgress =
      freshAccount?.osintEnabled &&
      freshAccount?.osintScrapingStatus === 'in-progress';
    setShouldPoll(isOpen && !!inProgress);
  }, [isOpen, freshAccount?.osintEnabled, freshAccount?.osintScrapingStatus]);

  useEffect(() => {
    if (!isOpen || !selectedCampaignTemplate) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      campaignTemplateId: selectedCampaignTemplate.id,
      difficulty: selectedCampaignTemplate.difficulty,
      phishingSign: selectedCampaignTemplate.meta?.phishingSign,
      name: selectedCampaignTemplate.name,
    }));
  }, [selectedCampaignTemplate, isOpen]);

  const targetGroupKey = (formData.targetGroup ?? [])
    .map((t) => t.id)
    .join(',');

  useEffect(() => {
    if (!isOpen || !currentAccount?.id) return;

    client.planner
      .targetSelectorPreview(
        {
          ...getTargetsAndExcludes(formData.targetGroup ?? [], []),
          domainVerifiedOnly: true,
        },
        currentAccount.id,
      )
      .then((res) =>
        setExcludedDomainUnverifiedCount(res.excludedDomainUnverifiedCount),
      )
      .catch(() => setExcludedDomainUnverifiedCount(0));
  }, [targetGroupKey, isOpen, currentAccount?.id]);

  const isToday = (date?: Date | null): boolean => {
    if (!date) return false;

    const today = moment().startOf('day');
    const dateToCheck = moment(date).startOf('day');
    return today.isSame(dateToCheck, 'day');
  };

  const getDefaultStartTime = (date: Date | null): Date | undefined => {
    if (!date) return undefined;
    const dateMoment = moment(date);

    if (isToday(date)) {
      const baseHour = 9;
      const now = moment();
      const defaultTime = dateMoment
        .hour(baseHour)
        .minute(0)
        .second(0)
        .millisecond(0);

      if (defaultTime.isBefore(now)) {
        return now.clone().add(60, 'minutes').toDate();
      }

      return defaultTime.toDate();
    }

    return dateMoment.hour(9).minute(0).second(0).millisecond(0).toDate();
  };

  const getDefaultEndTime = (
    startDate: Date | null,
    endDate: Date | null,
  ): Date | undefined => {
    if (!startDate || !endDate) return undefined;

    const startMoment = moment(startDate);
    const endMoment = moment(endDate);

    if (isToday(startDate) && isToday(endDate)) {
      const defaultTime = endMoment.hour(15).minute(0).second(0).millisecond(0);

      const minEndTime = startMoment
        .clone()
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);
      if (defaultTime.isBefore(minEndTime)) {
        return minEndTime.toDate();
      }

      return defaultTime.toDate();
    }

    const minEndTime = startMoment.clone().add(1, 'hour');
    if (endMoment.isBefore(minEndTime)) {
      return minEndTime.toDate();
    }

    if (startMoment.isSame(endMoment, 'day')) {
      return endMoment.hour(15).minute(0).second(0).millisecond(0).toDate();
    }

    return endDate;
  };

  const getEndDateMinDate = (): Date => {
    if (!formData.startDate) {
      return moment().toDate();
    }

    return moment(formData.startDate).add(1, 'hour').toDate();
  };

  const handleStartDateChange = (value: Date | null) => {
    const newStartDate = value ?? undefined;
    let newEndDate = formData.endDate;

    if (newStartDate) {
      const prevDate = formData.startDate;
      const dateChanged =
        !prevDate || !moment(newStartDate).isSame(moment(prevDate), 'day');

      if (dateChanged) {
        if (isToday(newStartDate)) {
          const defaultStartTime = getDefaultStartTime(newStartDate);
          if (defaultStartTime) {
            newStartDate.setHours(defaultStartTime.getHours());
            newStartDate.setMinutes(defaultStartTime.getMinutes());
            newStartDate.setSeconds(0);
            newStartDate.setMilliseconds(0);
          }
        } else {
          newStartDate.setHours(9);
          newStartDate.setMinutes(0);
          newStartDate.setSeconds(0);
          newStartDate.setMilliseconds(0);
        }
      }
    }

    if (newStartDate && newEndDate) {
      const minEndTime = moment(newStartDate)
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);
      if (moment(newEndDate).isBefore(minEndTime)) {
        if (isToday(newStartDate) && isToday(newEndDate)) {
          const adjustedEndDate = getDefaultEndTime(newStartDate, newEndDate);
          if (adjustedEndDate) {
            newEndDate = adjustedEndDate;
          }
        } else {
          newEndDate = minEndTime.toDate();
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      startDate: newStartDate,
      endDate: newEndDate,
    }));
  };

  const handleEndDateChange = (value: Date | null) => {
    const newEndDate = value ?? undefined;
    const startDate = formData.startDate;

    if (newEndDate) {
      const prevEndDate = formData.endDate;
      const dateChanged =
        !prevEndDate || !moment(newEndDate).isSame(moment(prevEndDate), 'day');

      if (dateChanged) {
        if (startDate && isToday(startDate) && isToday(newEndDate)) {
          const defaultEndTime = getDefaultEndTime(startDate, newEndDate);
          if (defaultEndTime) {
            newEndDate.setHours(defaultEndTime.getHours());
            newEndDate.setMinutes(defaultEndTime.getMinutes());
            newEndDate.setSeconds(0);
            newEndDate.setMilliseconds(0);
          }
        } else if (startDate && newEndDate) {
          if (!isToday(newEndDate)) {
            newEndDate.setHours(15);
            newEndDate.setMinutes(0);
            newEndDate.setSeconds(0);
            newEndDate.setMilliseconds(0);
          }
        } else if (!startDate) {
          if (!isToday(newEndDate)) {
            newEndDate.setHours(15);
            newEndDate.setMinutes(0);
            newEndDate.setSeconds(0);
            newEndDate.setMilliseconds(0);
          }
        }
      }

      if (startDate && newEndDate) {
        const minEndTime = moment(startDate).add(1, 'hour');
        if (moment(newEndDate).isBefore(minEndTime)) {
          const adjustedEndTime = minEndTime.toDate();
          newEndDate.setHours(adjustedEndTime.getHours());
          newEndDate.setMinutes(adjustedEndTime.getMinutes());
          newEndDate.setSeconds(0);
          newEndDate.setMilliseconds(0);
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      endDate: newEndDate,
    }));
  };

  const handleSubmit = () => {
    onPlanSimulation(formData);
    setFormData({
      campaignTemplateId: undefined,
      startDate: undefined,
      endDate: undefined,
      targetGroup: getDefaultTargetGroup(currentAccount as Account),
      difficulty: undefined,
      phishingSign: undefined,
      name: undefined,
    });
    onClose();
  };

  const handleClose = () => {
    setFormData({
      id: undefined,
      campaignTemplateId: undefined,
      difficulty: undefined,
      phishingSign: undefined,
      name: undefined,
      startDate: undefined,
      endDate: undefined,
      targetGroup: getDefaultTargetGroup(currentAccount as Account),
    });
    onClose();
  };

  useEffect(() => {
    if (selectedTask) {
      setFormData({
        id: selectedTask.id,
        campaignTemplateId: selectedTask.campaignTemplateId,
        difficulty: selectedTask.difficulty,
        phishingSign: selectedTask.phishingSign,
        name: selectedTask.name,
        startDate: selectedTask.startDate ?? undefined,
        endDate: selectedTask.endDate ?? undefined,
        targetGroup: processIncludeExcludeTargetEntities({
          target: selectedTask.targetGroup ?? {},
          excludes: {},
          account: currentAccount as Account,
        }).includedEntities,
        isGeneratedWithAI: selectedTask.isGeneratedWithAI,
      });
    } else if (!selectedCampaignTemplate) {
      setFormData({
        campaignTemplateId: undefined,
        difficulty: undefined,
        phishingSign: undefined,
        name: undefined,
        startDate: undefined,
        endDate: undefined,
        targetGroup: getDefaultTargetGroup(currentAccount as Account),
        id: undefined,
      });
    }
  }, [selectedTask, isOpen, selectedCampaignTemplate]);

  return (
    <>
      <StyledDialog
        visible={isOpen && !showDpaAcceptanceModal && !showAISimulationModal}
        onHide={handleClose}
        header={
          selectedTask
            ? t('phishing.simulations.modal.edit.title')
            : t('phishing.simulations.modal.plan.title')
        }
        modal
        draggable={false}
        resizable={false}
        className="v2"
      >
        <div className="field w-full mb-4">
          <label>{t('phishing.simulations.modal.chooseSimulation')}</label>
          <SimulationSelectorContainer>
            {formData.campaignTemplateId ? (
              <SimulationTemplateDisplay>
                <SimulationTemplateCard
                  difficulty={formData.difficulty}
                  phishingSign={formData.phishingSign}
                  name={formData.name}
                  isGeneratedWithAI={formData.isGeneratedWithAI}
                />
                {!formData.id &&
                  !isRecommendedSimulation &&
                  !formData.isGeneratedWithAI && (
                    <EditIconButton
                      onClick={() => {
                        setShowCampaignTemplatesModal(true);
                      }}
                    >
                      <EditIcon />
                    </EditIconButton>
                  )}
              </SimulationTemplateDisplay>
            ) : (
              <FlexContainer gap={18}>
                <AddButton
                  title={t('phishing.simulations.modal.browseCatalogue')}
                  description={t(
                    'phishing.simulations.modal.browseCatalogue.descr',
                  )}
                  id="browse-catalogue"
                  onClick={() => {
                    setShowCampaignTemplatesModal(true);
                  }}
                  isModal
                />
                {canUseFeature(
                  Subjects.RECON_AI_GENERATED_CAMPAIGN_TEMPLATE,
                ) && (
                  <AddButton
                    title={
                      freshAccount?.osintEnabled &&
                      effectiveStatus === 'in-progress'
                        ? t('phishing.simulations.modal.reconai.preparing')
                        : freshAccount?.osintEnabled &&
                          effectiveStatus === 'failed'
                        ? t('phishing.simulations.modal.reconai.failed')
                        : t('phishing.simulations.modal.generateSimulation')
                    }
                    description={
                      freshAccount?.osintEnabled &&
                      (effectiveStatus === 'in-progress' ||
                        effectiveStatus === 'failed')
                        ? ''
                        : t(
                            'phishing.simulations.modal.generateSimulation.descr',
                          )
                    }
                    id="generate-simulation"
                    disabled={
                      freshAccount?.osintEnabled &&
                      effectiveStatus === 'in-progress'
                    }
                    onClick={async () => {
                      if (!freshAccount?.osintEnabled) {
                        setShowDpaAcceptanceModal(true);
                      } else if (effectiveStatus === 'failed') {
                        try {
                          await acceptDpa();
                          await refetchAccount();
                          toast?.success(
                            t('toast.success'),
                            t('osint.dpa.accepted.success'),
                          );
                        } catch (error) {
                          handleAxiosError(error as Error | AxiosError, toast);
                        }
                      } else {
                        setShowAISimulationModal(true);
                      }
                    }}
                    chipLabel={
                      !freshAccount?.osintEnabled ||
                      effectiveStatus === 'complete'
                        ? t('phishing.simulations.modal.reconai.chip')
                        : undefined
                    }
                    isModal
                  />
                )}
              </FlexContainer>
            )}
          </SimulationSelectorContainer>
        </div>

        <div>
          <DateRow>
            <div className="field w-full mb-4">
              <label>
                {t('generic.startDate')} <span className="red"> *</span>
              </label>
              <AppCalendarWithCustomTimepicker
                value={formData.startDate}
                onChange={handleStartDateChange}
                minDate={moment().toDate()}
                placeholder={t('generic.select.startDate')}
                className="w-full"
                showTime
                withHolidays
              />
            </div>
            <div className="field w-full mb-4">
              <label>
                {t('generic.endDate')} <span className="red"> *</span>
              </label>
              <AppCalendarWithCustomTimepicker
                value={formData.endDate}
                onChange={handleEndDateChange}
                minDate={getEndDateMinDate()}
                maxDate={moment(formData.startDate ?? new Date())
                  .add(11, 'months')
                  .toDate()}
                placeholder={t('generic.select.endDate')}
                className="w-full"
                viewDate={formData.endDate ?? formData.startDate}
                showTime
                withHolidays
              />
            </div>
          </DateRow>
        </div>

        <div className="field w-full mb-4">
          <TargetEntitiesSelector
            includedEntities={
              formData.targetGroup ??
              getDefaultTargetGroup(currentAccount as Account)
            }
            className="phishing"
            title={t('phishing.simulations.modal.targetGroup')}
            required
            setIncludedEntities={(values: TargetFormValue[]) =>
              setFormData((prev) => ({
                ...prev,
                targetGroup: values,
              }))
            }
            excludedEntities={[]}
            setExcludedEntities={undefined}
            hideExcluding
          />
          {excludedDomainUnverifiedCount > 0 && (
            <StyledUnverifiedDomainWarning>
              <WarningTriangleIcon />
              <div className="warning-text">
                <p>
                  <strong>
                    {t('campaign.target.domainUnverified.count', {
                      count: excludedDomainUnverifiedCount,
                    })}
                  </strong>{' '}
                  {t('campaign.target.domainUnverified.description')}
                </p>
                <div>
                  <a
                    target="_blank"
                    className="warning-link"
                    onClick={() =>
                      window.open(
                        getAppAbsoluteUrl(
                          RedirectPaths[
                            RedirectPathsEnum
                              .ACCOUNT_EMAILS_AND_DELIVERY_DOMAINS
                          ](),
                        ),
                        '_blank',
                      )
                    }
                  >
                    {t('campaign.target.domainUnverified.link')}
                  </a>
                </div>
              </div>
            </StyledUnverifiedDomainWarning>
          )}
        </div>

        {selectedTask?.id &&
          selectedTask?.emailTemplateId &&
          selectedTask?.sendingProfileId &&
          selectedTask?.landingPageId && (
            <div className="mb-4">
              <StyledAccordion>
                <AccordionTab header={t('simulations.testEmail.title')}>
                  <SendTestEmailForm
                    campaignId={selectedTask.id}
                    emailTemplateId={selectedTask.emailTemplateId}
                    sendingProfileId={selectedTask.sendingProfileId}
                    landingPageId={selectedTask.landingPageId}
                  />
                </AccordionTab>
              </StyledAccordion>
            </div>
          )}

        <FlexContainer justify="flex-start" gap={12}>
          <AppButton
            label={
              selectedTask
                ? t('phishing.simulations.modal.saveChanges')
                : t('phishing.simulations.modal.planSimulation')
            }
            isDisabled={
              !formData.campaignTemplateId ||
              !formData.startDate ||
              !formData.endDate
            }
            onClick={isDemoAccount ? undefined : handleSubmit}
          />
          <AppButton
            severity="secondary"
            type="outlined"
            label={t('button.cancel')}
            onClick={handleClose}
          />
        </FlexContainer>
      </StyledDialog>
      <CatalogueSelectorModal
        visible={showCampaignTemplatesModal}
        onHide={() => setShowCampaignTemplatesModal(false)}
        onSelect={(template) => {
          setFormData((prev) => ({
            ...prev,
            campaignTemplateId: template.id,
            difficulty: template.difficulty ?? undefined,
            phishingSign: template.phishingSign ?? undefined,
            name: template.name,
          }));
          setShowCampaignTemplatesModal(false);
        }}
        entryPoint="phishing-simulation"
      />
      {canUseFeature(Subjects.RECON_AI_GENERATED_CAMPAIGN_TEMPLATE) && (
        <>
          <DpaAcceptanceModal
            isOpen={showDpaAcceptanceModal}
            onClose={() => setShowDpaAcceptanceModal(false)}
            onAccepted={() => setShowDpaAcceptanceModal(false)}
            accountId={currentAccount?.id ?? ''}
          />
          <PlanWithAssistanceModal
            isOpen={showAISimulationModal}
            onClose={() => setShowAISimulationModal(false)}
            isSingleSimulation
            onUseEmailTemplate={(campaignTemplate?: CampaignTemplate) => {
              setFormData((prev) => ({
                ...prev,
                campaignTemplateId: campaignTemplate?.id,
                difficulty: campaignTemplate?.difficulty ?? undefined,
                phishingSign: campaignTemplate?.meta?.phishingSign ?? undefined,
                name: campaignTemplate?.name,
                isGeneratedWithAI: true,
              }));
              setShowAISimulationModal(false);
            }}
          />
        </>
      )}
    </>
  );
};
